import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, Plus, Eye, RefreshCw, Calendar, Building2, 
  Download, TrendingUp, TrendingDown, IndianRupee, ArrowLeft,
  BookOpen, FileText, Users
} from 'lucide-react';
import useProtectedExport from '../hooks/useProtectedExport';
import ExportPasswordDialog from '../components/ExportPasswordDialog';

const CustomerLedger = () => {
  const { user } = useAuth();

  // View modes: 'list' or 'detail'
  const [viewMode, setViewMode] = useState('list');
  const protectedExport = useProtectedExport();
  
  // Data states
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerSummary, setLedgerSummary] = useState({});
  const [stats, setStats] = useState({});
  
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [hasBalanceFilter, setHasBalanceFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Dialog states
  const [manualEntryDialogOpen, setManualEntryDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    transaction_type: 'debit',
    amount: '',
    transaction_date: new Date().toISOString().split('T')[0],
    particulars: '',
    notes: '',
    reference_number: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Role checks
  const isAdmin = user?.role === 'Admin';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const canAccess = isAdmin || isBackOfficeAccounts;

  // Fetch customers with balance
  const fetchCustomers = useCallback(async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (hasBalanceFilter === 'with') params.has_balance = true;
      if (hasBalanceFilter === 'without') params.has_balance = false;
      
      const response = await api.get('/customer-ledger/customers', { params });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [searchTerm, hasBalanceFilter]);

  // Fetch ledger stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/customer-ledger/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch customer ledger
  const fetchCustomerLedger = useCallback(async () => {
    if (!selectedCustomer) return;
    
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      
      const response = await api.get(`/customer-ledger/customer/${selectedCustomer.id}`, { params });
      setLedgerEntries(response.data.entries || []);
      setLedgerSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching ledger:', error);
      toast.error('Failed to load ledger');
    }
  }, [selectedCustomer, fromDate, toDate]);

  useEffect(() => {
    if (canAccess && viewMode === 'list') {
      fetchCustomers();
      fetchStats();
    }
  }, [canAccess, viewMode, fetchCustomers, fetchStats]);

  useEffect(() => {
    if (viewMode === 'detail') {
      fetchCustomerLedger();
    }
  }, [viewMode, fetchCustomerLedger]);

  const handleRefresh = () => {
    setLoading(true);
    if (viewMode === 'list') {
      Promise.all([fetchCustomers(), fetchStats()])
        .finally(() => setLoading(false));
    } else {
      fetchCustomerLedger()
        .finally(() => setLoading(false));
    }
  };

  // View customer ledger
  const handleViewLedger = (customer) => {
    setSelectedCustomer(customer);
    setFromDate('');
    setToDate('');
    setViewMode('detail');
  };

  // Go back to list
  const handleBackToList = () => {
    setViewMode('list');
    setSelectedCustomer(null);
    setLedgerEntries([]);
    setLedgerSummary({});
  };

  // Export ledger to CSV
  const handleExportCSV = async () => {
    if (!selectedCustomer) return;
    
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      
      const response = await api.get(`/customer-ledger/customer/${selectedCustomer.id}/export/csv`, {
        params,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ledger_${selectedCustomer.company_name?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Ledger exported successfully');
    } catch (error) {
      toast.error('Failed to export ledger');
    }
  };

  // Open manual entry dialog
  const openManualEntryDialog = () => {
    setFormData({
      customer_id: selectedCustomer?.id || '',
      transaction_type: 'debit',
      amount: '',
      transaction_date: new Date().toISOString().split('T')[0],
      particulars: '',
      notes: '',
      reference_number: ''
    });
    setManualEntryDialogOpen(true);
  };

  // Handle manual entry submit
  const handleManualEntrySubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error('Customer is required');
      return;
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!formData.particulars || formData.particulars.trim().length < 3) {
      toast.error('Particulars are required (minimum 3 characters)');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/customer-ledger/manual-entry', {
        customer_id: formData.customer_id,
        transaction_type: formData.transaction_type,
        amount: parseFloat(formData.amount),
        transaction_date: formData.transaction_date,
        particulars: formData.particulars.trim(),
        notes: formData.notes || '',
        reference_number: formData.reference_number || ''
      });
      
      toast.success('Ledger entry added successfully');
      setManualEntryDialogOpen(false);
      
      if (viewMode === 'detail') {
        fetchCustomerLedger();
      } else {
        fetchCustomers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>
              Only Admin and Backoffice Accounts Executive can access Customer Ledger.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // CUSTOMER LIST VIEW
  if (viewMode === 'list') {
    return (
      <div className="space-y-6" data-testid="customer-ledger-list">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Customer Ledger</h1>
            <p className="text-gray-600 mt-1">Track customer balances and transactions</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRefresh} disabled={loading} variant="outline">
              <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={openManualEntryDialog} variant="outline">
              <Plus size={18} className="mr-2" />
              Manual Entry
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-r from-red-50 to-red-100 border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Total Receivable</p>
                  <p className="text-3xl font-bold text-red-800">₹{(stats.total_receivable || 0).toLocaleString()}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-red-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Customers with Balance</p>
                  <p className="text-3xl font-bold text-blue-800">{stats.customers_with_balance || 0}</p>
                </div>
                <Users className="h-10 w-10 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Today's Credit</p>
                  <p className="text-3xl font-bold text-green-800">₹{(stats.today_credit || 0).toLocaleString()}</p>
                </div>
                <TrendingDown className="h-10 w-10 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Today's Debit</p>
                  <p className="text-3xl font-bold text-orange-800">₹{(stats.today_debit || 0).toLocaleString()}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-orange-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Search customer by name, mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          <Select value={hasBalanceFilter} onValueChange={setHasBalanceFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Balance Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              <SelectItem value="with">With Balance</SelectItem>
              <SelectItem value="without">Zero Balance</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Customers Table */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Balances</CardTitle>
            <CardDescription>Click on a customer to view detailed ledger</CardDescription>
          </CardHeader>
          <CardContent>
            {customers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No customers found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer Name</TableHead>
                      <TableHead>Contact Person</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead className="text-right">Current Balance</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow 
                        key={customer.id} 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleViewLedger(customer)}
                        data-testid={`customer-${customer.id}`}
                      >
                        <TableCell className="font-medium">{customer.company_name}</TableCell>
                        <TableCell>{customer.contact_person || '-'}</TableCell>
                        <TableCell>{customer.mobile}</TableCell>
                        <TableCell>{customer.city || '-'}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${customer.current_balance > 0 ? 'text-red-600' : customer.current_balance < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                            ₹{Math.abs(customer.current_balance || 0).toLocaleString()}
                            {customer.current_balance > 0 && ' Dr'}
                            {customer.current_balance < 0 && ' Cr'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLedger(customer);
                            }}
                          >
                            <Eye size={14} className="mr-1" />
                            View Ledger
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Manual Entry Dialog */}
        <Dialog open={manualEntryDialogOpen} onOpenChange={setManualEntryDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Manual Ledger Entry</DialogTitle>
              <DialogDescription>
                Add opening balance, adjustments, or other manual entries
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleManualEntrySubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Customer *</Label>
                  <Select 
                    value={formData.customer_id} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, customer_id: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Transaction Type *</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.transaction_type === 'debit' ? 'default' : 'outline'}
                      className={formData.transaction_type === 'debit' ? 'bg-red-600' : ''}
                      onClick={() => setFormData(prev => ({ ...prev, transaction_type: 'debit' }))}
                    >
                      <TrendingUp size={16} className="mr-2" />
                      Debit (Dr)
                    </Button>
                    <Button
                      type="button"
                      variant={formData.transaction_type === 'credit' ? 'default' : 'outline'}
                      className={formData.transaction_type === 'credit' ? 'bg-green-600' : ''}
                      onClick={() => setFormData(prev => ({ ...prev, transaction_type: 'credit' }))}
                    >
                      <TrendingDown size={16} className="mr-2" />
                      Credit (Cr)
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Amount (₹) *</Label>
                  <Input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="Enter amount"
                    min="1"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Transaction Date *</Label>
                  <Input
                    type="date"
                    value={formData.transaction_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, transaction_date: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Particulars *</Label>
                  <Input
                    value={formData.particulars}
                    onChange={(e) => setFormData(prev => ({ ...prev, particulars: e.target.value }))}
                    placeholder="e.g., Opening Balance, Adjustment, etc."
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Reference Number</Label>
                  <Input
                    value={formData.reference_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                    placeholder="Optional reference"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes..."
                    rows={2}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setManualEntryDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className={formData.transaction_type === 'debit' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                >
                  {submitting ? 'Adding...' : `Add ${formData.transaction_type === 'debit' ? 'Debit' : 'Credit'} Entry`}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // LEDGER DETAIL VIEW
  return (
    <div className="space-y-6" data-testid="customer-ledger-detail">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={handleBackToList}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{selectedCustomer?.company_name}</h1>
            <p className="text-gray-600 mt-1">
              {selectedCustomer?.mobile} • {selectedCustomer?.city}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRefresh} disabled={loading} variant="outline">
            <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openManualEntryDialog} variant="outline">
            <Plus size={18} className="mr-2" />
            Add Entry
          </Button>
          <Button onClick={() => protectedExport.requestExport(handleExportCSV)} variant="outline">
            <Download size={18} className="mr-2" />
            Export PDF/CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Opening Balance</p>
              <p className="text-2xl font-bold text-gray-800">₹{(ledgerSummary.opening_balance || 0).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm font-medium text-red-600">Total Debit</p>
              <p className="text-2xl font-bold text-red-800">₹{(ledgerSummary.total_debit || 0).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm font-medium text-green-600">Total Credit</p>
              <p className="text-2xl font-bold text-green-800">₹{(ledgerSummary.total_credit || 0).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>

        <Card className={`${ledgerSummary.closing_balance > 0 ? 'bg-red-100 border-red-300' : 'bg-green-100 border-green-300'}`}>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className={`text-sm font-medium ${ledgerSummary.closing_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                Closing Balance
              </p>
              <p className={`text-2xl font-bold ${ledgerSummary.closing_balance > 0 ? 'text-red-800' : 'text-green-800'}`}>
                ₹{Math.abs(ledgerSummary.closing_balance || 0).toLocaleString()}
                {ledgerSummary.closing_balance > 0 && ' Dr'}
                {ledgerSummary.closing_balance < 0 && ' Cr'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Date Filters */}
      <div className="flex gap-4 items-center">
        <span className="text-sm text-gray-500">Filter by date:</span>
        <div className="flex gap-2 items-center">
          <Calendar size={18} className="text-gray-400" />
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-40"
          />
          <span className="text-gray-400">to</span>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-40"
          />
          {(fromDate || toDate) && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setFromDate(''); setToDate(''); }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Ledger Table */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
          <CardDescription>All transactions for this customer</CardDescription>
        </CardHeader>
        <CardContent>
          {ledgerEntries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No transactions found</p>
              <Button onClick={openManualEntryDialog} className="mt-4" variant="outline">
                <Plus size={16} className="mr-2" />
                Add First Entry
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Particulars</TableHead>
                    <TableHead className="text-right">Debit (₹)</TableHead>
                    <TableHead className="text-right">Credit (₹)</TableHead>
                    <TableHead className="text-right">Balance (₹)</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledgerEntries.map((entry, index) => (
                    <TableRow key={entry.id || index} data-testid={`ledger-entry-${index}`}>
                      <TableCell className="whitespace-nowrap">
                        {entry.transaction_date}
                      </TableCell>
                      <TableCell className="font-medium">
                        {entry.particulars}
                        {entry.is_manual && (
                          <Badge variant="outline" className="ml-2 text-xs">Manual</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {entry.debit > 0 ? `₹${entry.debit.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {entry.credit > 0 ? `₹${entry.credit.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${entry.closing_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ₹{Math.abs(entry.closing_balance || 0).toLocaleString()}
                        {entry.closing_balance > 0 ? ' Dr' : entry.closing_balance < 0 ? ' Cr' : ''}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500 max-w-[150px] truncate" title={entry.notes}>
                        {entry.notes || '-'}
                      </TableCell>
                      <TableCell>
                        {entry.reference_id ? (
                          <span className="text-blue-600 text-sm">{entry.reference_id}</span>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Entry Dialog (same as list view) */}
      <Dialog open={manualEntryDialogOpen} onOpenChange={setManualEntryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Manual Ledger Entry</DialogTitle>
            <DialogDescription>
              Adding entry for {selectedCustomer?.company_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleManualEntrySubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Transaction Type *</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={formData.transaction_type === 'debit' ? 'default' : 'outline'}
                    className={formData.transaction_type === 'debit' ? 'bg-red-600' : ''}
                    onClick={() => setFormData(prev => ({ ...prev, transaction_type: 'debit' }))}
                  >
                    <TrendingUp size={16} className="mr-2" />
                    Debit (Dr)
                  </Button>
                  <Button
                    type="button"
                    variant={formData.transaction_type === 'credit' ? 'default' : 'outline'}
                    className={formData.transaction_type === 'credit' ? 'bg-green-600' : ''}
                    onClick={() => setFormData(prev => ({ ...prev, transaction_type: 'credit' }))}
                  >
                    <TrendingDown size={16} className="mr-2" />
                    Credit (Cr)
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="Enter amount"
                  min="1"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Transaction Date *</Label>
                <Input
                  type="date"
                  value={formData.transaction_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, transaction_date: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Particulars *</Label>
                <Input
                  value={formData.particulars}
                  onChange={(e) => setFormData(prev => ({ ...prev, particulars: e.target.value }))}
                  placeholder="e.g., Opening Balance, Adjustment, etc."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Reference Number</Label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                  placeholder="Optional reference"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setManualEntryDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className={formData.transaction_type === 'debit' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
              >
                {submitting ? 'Adding...' : `Add ${formData.transaction_type === 'debit' ? 'Debit' : 'Credit'} Entry`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ExportPasswordDialog {...protectedExport} />
    </div>
  );
};

export default CustomerLedger;
